import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  ROAD_WIDTHS,
  BRIDGE_LIFT_BASE,
  BRIDGE_LIFT_PER_LAYER,
  BRIDGE_PILLAR_SPACING,
  BRIDGE_DECK_COLOR,
  BRIDGE_PILLAR_COLOR,
} from '../constants.js';
import { buildQuadStrip, projectAndDeduplicate } from './roads.js';

/**
 * Build bridge deck and pillar geometry for all bridged roads.
 * The road surface elevation is already handled in roads.js; this module
 * adds the concrete deck (slightly wider, darker strip) and vertical pillars.
 *
 * @param {Array}    roads   Full roads array (filtered internally to bridge=true)
 * @param {Object}   proj    Projection helper
 * @param {Function} elevFn  Optional: elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildBridgeMesh(roads, proj, elevFn = null) {
  const deckGeos = [];
  const group    = new THREE.Group();

  for (const road of roads) {
    if (!road.bridge) continue;

    const pts = projectAndDeduplicate(road.nodes, proj);
    if (pts.length < 2) continue;

    const lift  = BRIDGE_LIFT_BASE + Math.max(0, (road.layer - 1)) * BRIDGE_LIFT_PER_LAYER;
    const bridgeElev = (x, z) => (elevFn ? elevFn(x, z) : 0) + lift;

    const halfW  = road.width ? road.width / 2 : (ROAD_WIDTHS[road.highway] ?? ROAD_WIDTHS.default) / 2;
    const deckHW = halfW + 0.6; // slightly wider than road

    // Deck strip
    const deckGeo = buildQuadStrip(pts, deckHW, bridgeElev);
    if (deckGeo) deckGeos.push(deckGeo);

    // Pillars: walk centreline at BRIDGE_PILLAR_SPACING intervals
    _addPillars(group, pts, lift, elevFn);
  }

  if (deckGeos.length > 0) {
    const merged = mergeGeometries(deckGeos, false);
    deckGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshLambertMaterial({ color: BRIDGE_DECK_COLOR });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  return group;
}

// ── Pillar generation ─────────────────────────────────────────────────────────

function _addPillars(group, pts, lift, elevFn) {
  const pillarMat = new THREE.MeshLambertMaterial({ color: BRIDGE_PILLAR_COLOR });
  let accum = BRIDGE_PILLAR_SPACING * 0.5; // offset first pillar from start

  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const dx = b.x - a.x, dz = b.z - a.z;
    const segLen = Math.sqrt(dx * dx + dz * dz);
    if (segLen < 0.01) continue;

    let dist = accum === 0 ? 0 : BRIDGE_PILLAR_SPACING - accum;
    while (dist <= segLen) {
      const t  = dist / segLen;
      const cx = a.x + dx * t;
      const cz = a.z + dz * t;

      const groundY  = elevFn ? elevFn(cx, cz) : 0;
      const bridgeY  = groundY + lift;
      const pillarH  = bridgeY - groundY;

      if (pillarH > 0.5) {
        const geo  = new THREE.BoxGeometry(1.2, pillarH, 1.2);
        const mesh = new THREE.Mesh(geo, pillarMat);
        mesh.position.set(cx, groundY + pillarH * 0.5, cz);
        mesh.castShadow = mesh.receiveShadow = true;
        group.add(mesh);
      }

      dist += BRIDGE_PILLAR_SPACING;
    }

    accum = segLen - (dist - BRIDGE_PILLAR_SPACING);
  }
}
